import { usePage } from "@inertiajs/react"
import { Plus } from "lucide-react"

import type { StoryGroup } from "@/types/serializers"

export type { StoryGroup }

interface StoryRowProps {
  stories: StoryGroup[]
  onViewStories: (groupIndex: number) => void
  onAddStory: () => void
}

export default function StoryRow({
  stories,
  onViewStories,
  onAddStory,
}: StoryRowProps) {
  const { auth } = usePage().props

  const currentUserId = auth.user.id
  const hasOwnStories = stories.some((group) => group.user.id === currentUserId)

  return (
    <div className="mb-4 flex gap-3 overflow-x-auto pb-2">
      {/* Own avatar — always first */}
      {!hasOwnStories && (
        <button
          onClick={onAddStory}
          className="flex flex-shrink-0 flex-col items-center gap-1"
        >
          <div className="relative" data-testid="story-ring">
            <div className="border-muted-foreground/40 bg-secondary text-muted-foreground flex h-16 w-16 items-center justify-center rounded-full border-2 border-dashed text-sm font-bold">
              {auth.user.first_name[0]}
              {auth.user.last_name[0]}
            </div>
            <div
              data-testid="add-story-icon"
              className="bg-krown-400 absolute -right-0.5 -bottom-0.5 flex h-5 w-5 items-center justify-center rounded-full text-white"
            >
              <Plus className="h-3 w-3" />
            </div>
          </div>
          <span className="text-muted-foreground max-w-[64px] truncate text-xs">
            {auth.user.first_name}
          </span>
        </button>
      )}

      {stories.map((group, index) => {
        const isOwn = group.user.id === currentUserId
        const initials = `${group.user.first_name[0]}${group.user.last_name[0]}`

        return (
          <div
            key={group.user.id}
            role="button"
            tabIndex={0}
            onClick={() => onViewStories(index)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onViewStories(index)
            }}
            className="flex flex-shrink-0 cursor-pointer flex-col items-center gap-1"
          >
            <div className="relative" data-testid="story-ring">
              <div className="from-krown-accent via-krown-200 to-krown-crown rounded-full bg-gradient-to-tr p-[2px]">
                <div className="border-background rounded-full border-2">
                  {group.user.avatar_url ? (
                    <img
                      src={group.user.avatar_url}
                      alt={group.user.full_name}
                      className="h-14 w-14 rounded-full object-cover"
                    />
                  ) : (
                    <div className="bg-krown-200 flex h-14 w-14 items-center justify-center rounded-full text-sm font-bold text-white">
                      {initials}
                    </div>
                  )}
                </div>
              </div>
              {isOwn && (
                <button
                  data-testid="add-story-icon"
                  onClick={(e) => {
                    e.stopPropagation()
                    onAddStory()
                  }}
                  className="bg-krown-400 absolute -right-0.5 -bottom-0.5 flex h-5 w-5 items-center justify-center rounded-full text-white"
                >
                  <Plus className="h-3 w-3" />
                </button>
              )}
            </div>
            <span className="text-muted-foreground max-w-[64px] truncate text-xs">
              {group.user.first_name}
            </span>
          </div>
        )
      })}
    </div>
  )
}
